import { useEffect, useRef, useState } from 'react';

/** The slice of the Barcode Detection API this uses. Not yet in TypeScript's DOM lib. */
interface DetectedBarcode {
  rawValue: string;
}
interface BarcodeDetectorLike {
  detect(source: HTMLVideoElement): Promise<DetectedBarcode[]>;
}
type BarcodeDetectorCtor = new (options: { formats: string[] }) => BarcodeDetectorLike;

const Detector = (globalThis as { BarcodeDetector?: BarcodeDetectorCtor }).BarcodeDetector;

/**
 * Whether this browser can open a camera at all. It cannot on a plain-http page other than
 * localhost — a phone reaching the dev server by LAN address, say.
 */
export const cameraScanSupported = globalThis.isSecureContext === true;

/** The longest side a frame is shrunk to before jsQR reads it: plenty for a ticket held up close. */
const FALLBACK_SIZE = 640;

/**
 * A reader for one video frame: the browser's own where it has one, else jsQR — desktop Chrome
 * and Edge on Windows, and Firefox, have no Barcode Detection API. jsQR is fetched only then.
 */
async function frameReader(): Promise<(source: HTMLVideoElement) => Promise<string | null>> {
  if (Detector) {
    const detector = new Detector({ formats: ['qr_code'] });
    return async (source) => (await detector.detect(source))[0]?.rawValue ?? null;
  }
  const { default: jsQR } = await import('jsqr');
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d', { willReadFrequently: true });
  return async (source) => {
    if (!context || !source.videoWidth) return null;
    const scale = Math.min(1, FALLBACK_SIZE / Math.max(source.videoWidth, source.videoHeight));
    canvas.width = Math.round(source.videoWidth * scale);
    canvas.height = Math.round(source.videoHeight * scale);
    context.drawImage(source, 0, 0, canvas.width, canvas.height);
    const { data, width, height } = context.getImageData(0, 0, canvas.width, canvas.height);
    return jsQR(data, width, height, { inversionAttempts: 'dontInvert' })?.data ?? null;
  };
}

/** The same code held in front of the lens is read many times a second; one read is enough. */
const REPEAT_MS = 2500;

/**
 * The device camera, read for QR codes while `active`.
 *
 * Uses the browser's own Barcode Detection API where there is one, and jsQR
 * where there is not. Where the camera cannot be opened at all, the caller
 * hides this and the door uses a handheld scanner or types the code instead.
 */
export default function CameraScanner({
  active,
  onCode,
}: {
  active: boolean;
  onCode: (value: string) => void;
}) {
  const video = useRef<HTMLVideoElement>(null);
  const [problem, setProblem] = useState<string | null>(null);
  // Held in a ref so a new callback from the parent does not restart the camera.
  const handler = useRef(onCode);
  useEffect(() => {
    handler.current = onCode;
  }, [onCode]);

  useEffect(() => {
    if (!active || !cameraScanSupported) return;
    let stream: MediaStream | null = null;
    let frame = 0;
    let stopped = false;
    let last = { value: '', at: 0 };
    const reader = frameReader();

    const tick = async () => {
      if (stopped || !video.current) return;
      try {
        const code = await (await reader)(video.current);
        const now = Date.now();
        if (code && (code !== last.value || now - last.at > REPEAT_MS)) {
          last = { value: code, at: now };
          handler.current(code);
        }
      } catch {
        // A frame that is not ready yet; the next one will be.
      }
      if (!stopped) frame = requestAnimationFrame(() => void tick());
    };

    navigator.mediaDevices
      .getUserMedia({ video: { facingMode: 'environment' } })
      .then(async (s) => {
        if (stopped) return s.getTracks().forEach((t) => t.stop());
        stream = s;
        setProblem(null);
        if (video.current) {
          video.current.srcObject = s;
          await video.current.play();
          void tick();
        }
      })
      .catch(() => setProblem('The camera could not be opened. Check the browser permission.'));

    return () => {
      stopped = true;
      cancelAnimationFrame(frame);
      stream?.getTracks().forEach((t) => t.stop());
    };
  }, [active]);

  return (
    <>
      <video
        ref={video}
        muted
        playsInline
        className={active ? 'absolute inset-0 h-full w-full object-cover' : 'hidden'}
      />
      {active && problem && (
        <span className="relative px-8 text-center text-base text-on-panel">{problem}</span>
      )}
    </>
  );
}
